import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Mail, MessageCircle, ArrowLeft, ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const nav = useNavigate();
  const [channels, setChannels] = useState({ email_enabled: false, whatsapp_enabled: false });
  const [channel, setChannel] = useState("email");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  // OTP state
  const [otp, setOtp] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/reset-status").then((r) => {
      setChannels(r.data);
      // Auto-pick the first enabled channel
      if (!r.data.email_enabled && r.data.whatsapp_enabled) setChannel("whatsapp");
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, channel });
      setSent(true);
      toast.success(channel === "email"
        ? "If your email is registered, a reset link is on the way."
        : "If your WhatsApp is on file, an OTP has been sent."
      );
    } catch (err) {
      toast.error(apiError(err, "Could not send reset instructions"));
    } finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setOtpBusy(true);
    try {
      const r = await api.post("/auth/verify-otp", { email, otp });
      toast.success("OTP verified — set your new password");
      nav(`/reset-password?token=${encodeURIComponent(r.data.reset_token)}`);
    } catch (err) {
      toast.error(apiError(err, "OTP verification failed"));
    } finally { setOtpBusy(false); }
  };

  const bothEnabled = channels.email_enabled && channels.whatsapp_enabled;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex bg-ink text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center">
          <img src="/efgh-logo-white.png" alt="EFGH" className="h-12" />
        </Link>
        <div>
          <h2 className="font-heading font-bold text-4xl leading-tight">
            Locked out?<br /><span className="italic font-normal" style={{ color: "#F5C400" }}>We&apos;ll get you back in.</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-md">
            Choose how you&apos;d like to verify your identity — an email reset link or a WhatsApp OTP.
          </p>
        </div>
        <div className="text-white/40 text-xs">© 2026 EFGH Health Technologies</div>
      </div>

      <div className="p-6 sm:p-12 flex items-center justify-center">
        <Card className="w-full max-w-md p-8 rounded-2xl border-border" data-testid="forgot-password-card">
          <Link to="/login" data-testid="back-to-login" className="text-sm text-ink/60 hover:text-ink inline-flex items-center gap-1.5">
            <ArrowLeft className="w-4 h-4" /> Back to login
          </Link>
          <h1 className="mt-3 font-heading font-bold text-2xl text-ink">Reset your password</h1>
          <p className="mt-1 text-sm text-ink/60">
            Enter your account email. We&apos;ll {channel === "whatsapp" ? "send a 6-digit OTP to your WhatsApp" : "email you a secure reset link"}.
          </p>

          {/* Channel picker (only when both configured) */}
          {bothEnabled && (
            <div className="mt-5 grid grid-cols-2 gap-2" data-testid="channel-picker">
              <ChannelPill
                testid="channel-email"
                icon={Mail}
                label="Email link"
                sub="Instant"
                active={channel === "email"}
                onClick={() => { setChannel("email"); setSent(false); }}
              />
              <ChannelPill
                testid="channel-whatsapp"
                icon={MessageCircle}
                label="WhatsApp OTP"
                sub="6-digit code"
                active={channel === "whatsapp"}
                onClick={() => { setChannel("whatsapp"); setSent(false); }}
              />
            </div>
          )}
          {!channels.email_enabled && !channels.whatsapp_enabled && (
            <div className="mt-4 p-3 rounded-lg bg-warning-light text-warning text-sm">
              Password reset is not yet enabled on this deployment. Please ask your administrator.
            </div>
          )}

          {!sent ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  data-testid="forgot-email"
                  type="email"
                  required
                  autoFocus
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <Button
                data-testid="forgot-submit"
                type="submit"
                disabled={busy || !email || (!channels.email_enabled && !channels.whatsapp_enabled)}
                className="w-full h-11 bg-ink hover:bg-ink/90 text-white"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : (
                  channel === "whatsapp" ? <><MessageCircle className="w-4 h-4 mr-2" /> Send WhatsApp OTP</> : <><Mail className="w-4 h-4 mr-2" /> Send reset link</>
                )}
              </Button>
            </form>
          ) : channel === "whatsapp" ? (
            <form onSubmit={verifyOtp} className="mt-6 space-y-4" data-testid="otp-form">
              <div className="p-3 rounded-lg bg-success-light border border-success/20 text-success text-sm inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> OTP sent to your WhatsApp
              </div>
              <div>
                <Label>6-digit OTP</Label>
                <Input
                  data-testid="otp-input"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  autoFocus
                  className="text-center text-xl font-mono tracking-[0.4em] h-14"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <p className="text-xs text-ink/50 mt-1.5">Code expires in 10 minutes.</p>
              </div>
              <Button data-testid="otp-verify" type="submit" disabled={otpBusy || otp.length < 4} className="w-full h-11 bg-ink hover:bg-ink/90 text-white">
                {otpBusy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Verify and continue</>}
              </Button>
              <button
                type="button"
                data-testid="otp-resend"
                onClick={() => { setSent(false); setOtp(""); }}
                className="text-xs text-ink/60 hover:text-ink underline underline-offset-4 w-full text-center"
              >
                Didn&apos;t receive the OTP? Send again
              </button>
            </form>
          ) : (
            <div className="mt-6 p-5 rounded-xl bg-success-light border border-success/20" data-testid="email-sent-card">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-success shrink-0 mt-0.5" />
                <div>
                  <div className="font-heading font-semibold text-ink">Check your inbox</div>
                  <p className="text-sm text-ink/70 mt-1">
                    If <b>{email}</b> is registered with EFGH, we&apos;ve emailed a secure link to reset your password. It expires in 30 minutes.
                  </p>
                  <button
                    onClick={() => setSent(false)}
                    className="mt-3 text-xs text-ink/60 hover:text-ink underline underline-offset-4"
                    data-testid="resend-email"
                  >
                    Didn&apos;t get it? Try again
                  </button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function ChannelPill({ icon: Icon, label, sub, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`p-3 rounded-xl border text-left transition-all ${active ? "border-ink bg-ink/[0.03]" : "border-border hover:border-ink/40"}`}
    >
      <Icon className={`w-5 h-5 ${active ? "text-ink" : "text-ink/50"}`} />
      <div className={`mt-2 text-sm font-medium ${active ? "text-ink" : "text-ink/70"}`}>{label}</div>
      <div className="text-[11px] text-ink/50">{sub}</div>
    </button>
  );
}
